import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useIntl } from 'react-intl'

import {
  Typography,
  Box,
  TablePagination,
  Button,
  IconButton,
  Alert,
} from '@mui/material'

import { ReactComponent as Edit } from '../../../assets/icons/edit.svg'
import { ReactComponent as Trash } from '../../../assets/icons/trash.svg'
import useIsMobile from '../../../hooks/useIsMobile'
import SearchField from '../../../ui/components/SearchField'
import Loader from '../../../ui/views/Loader'
import SearchEmptyState from '../../../ui/modules/SearchEmptyState'
import { ReactComponent as ArrowRight } from '../../../assets/icons/arrow-right.svg'

import { addressPropType } from '../../../common/propTypes'
import useTranslations from '../../../i18n/useTranslations'
import Radio from '../../../ui/components/radio'
import { ADDRESS_LIST_ROWS_PER_PAGE_OPTIONS } from '../../../common/constants'

import useStyles from './styles'
import useDebouncedSearch from './useDebouncedSearch'

const DEBOUNCE_TIME_MS = 500

const AddressBook = ({
  isLoading,
  addresses,
  count,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  selected,
  hasPhone,
  validOrigin,
  onChange,
  onAddSearchFilter,
  onClickEditAddress,
  onClickDeleteAddress,
}) => {
  const classes = useStyles()
  const t = useTranslations()
  const intl = useIntl()

  const { value, handleChange, handleClear } = useDebouncedSearch(
    onAddSearchFilter,
    DEBOUNCE_TIME_MS,
  )

  const Pagination = () => (
    <TablePagination
      className={classes.pagination}
      component="div"
      count={count}
      page={page}
      onPageChange={onPageChange}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={onRowsPerPageChange}
      rowsPerPageOptions={ADDRESS_LIST_ROWS_PER_PAGE_OPTIONS}
      labelRowsPerPage={t('noun:contacts-per-page')}
    />
  )
  
  return (
    <Box className={classes.content}>
      <Box className={classes.radiosContainer}>
      {selected?.id &&
      !addresses.find(({ data }) => data?.id === selected?.id) ? (
        <Box key={selected.id} className={classes.radioSelectedWrapper}>
          <Radio
            className={classes.radio}
            label={selected.name}
            subLabel={t('address:without-name', {
              address: selected.address,
              postalCode: selected.postalCode,
              city: selected.city,
              country: intl.formatDisplayName(selected.country, {
                type: 'region',
              }),
            })}
            additionalText={
              selected.email || selected.phone
                ? `${selected.email || ''}${
                    selected.email && selected.phone ? ', ' : ''
                  }${selected.phone || ''}`
                : null
            }
            multiline={false}
            checked={true}
            onChange={() => onChange(selected)}
          />
          <Box key={selected.id} className={classes.buttons}>
            <IconButton
              onClick={() => onClickEditAddress(selected)}
              className={classes.iconButton}
            >
              <Edit />
            </IconButton>
            <IconButton
              onClick={() => onClickDeleteAddress(selected)}
              className={classes.iconButton}
            >
              <Trash />
            </IconButton>
          </Box>
        </Box>
      ) : null}
      </Box>
      
      <SearchField
        placeholder={t('placeholder:search-by-name-or-address-or-code')}
        className={classes.searchInput}
        id="search-field"
        value={value}
        onChange={e => handleChange(e.target.value)}
        onClear={handleClear}
      />
      {isLoading ? (
        <Loader />
      ) : !Boolean(addresses?.length) ? (
        <Box className={classes.emptyState}>
          <SearchEmptyState onClearSearch={handleClear} />
        </Box>
      ) : (
        <>
          <Box>
            <Box className={classes.radiosContainer}>
              {addresses.map(address => (
                <Box key={address.data.id} className={classes.radioWrapper}>
                  <Radio
                    className={classes.radio}
                    label={address.data.name}
                    subLabel={t('address:without-name', {
                      address: address.data.address,
                      postalCode: address.data.postalCode,
                      city: address.data.city,
                      country: intl.formatDisplayName(address.data.country, {
                        type: 'region',
                      }),
                    })}
                    additionalText={
                      address.data.email || address.data.phone
                        ? `${address.data.email || ''}${
                            address.data.email && address.data.phone ? ', ' : ''
                          }${address.data.phone || ''}`
                        : null
                    }
                    multiline={false}
                    checked={selected?.id === address.data?.id}
                    onChange={() => onChange({...address.data, ...address})}
                  />
                  <Box key={address.data.id} className={classes.buttons}>
                    <IconButton
                      onClick={() => onClickEditAddress(address)}
                      className={classes.iconButton}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      onClick={() => onClickDeleteAddress(address)}
                      className={classes.iconButton}
                    >
                      <Trash />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
            <Pagination />

            {!validOrigin && selected ? (
              <Alert severity="error" className={classes.alert}>
                <Typography component="p" variant="body2">
                  {t('error:origin-address:invalid-origin')}
                </Typography>
              </Alert>
            ) : null}
            {!hasPhone && selected && validOrigin ? (
              <Alert severity="error" className={classes.alert}>
                <Typography component="p" variant="body2">
                  {t('shipping-send-form:address:no-phone-error')}
                </Typography>
              </Alert>
            ) : null}
          </Box>
        </>
      )}
    </Box>
  )
}

AddressBook.propTypes = {
  isLoading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  addresses: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      address: PropTypes.string,
    }),
  ).isRequired,
  validOrigin: PropTypes.bool,
  hasPhone: PropTypes.bool,
  count: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  selected: addressPropType,
  onAddSearchFilter: PropTypes.func,
  onClickEditAddress: PropTypes.func,
  onClickDeleteAddress: PropTypes.func,
}

export default AddressBook
